/**
 * CertificateRequirements - Shows required certificates with click-to-add
 * Updated for WBC3 scope-aware requirements (FT-07)
 */
import React from 'react';
import { CheckCircle, AlertCircle, Plus, Info } from 'lucide-react';
import { getRequiredCertificates, isCertificateRequirementMet } from './certificateHelpers';

const CertificateRequirements = ({
  formData,
  position,
  vesselAreaCategory,
  vesselEnginePower,
  vesselLength,
  vesselEquipment,  // FT-30: Equipment config for conditional cert requirements
  onRequirementClick  // Click handler for adding certificates
}) => {
  const requirements = getRequiredCertificates(position, vesselAreaCategory, vesselEnginePower, vesselLength, vesselEquipment);

  // Render requirement with red alert (for mandatory certs)
  const renderRequirement = (cert, index) => {
    const isMet = isCertificateRequirementMet(cert, formData);

    return (
      <div
        key={index}
        className={`flex items-start gap-2 py-1.5 ${!isMet && onRequirementClick ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded' : ''}`}
        onClick={() => {
          if (!isMet && onRequirementClick) {
            // For requirements with options, pass the first option or let user choose
            const certType = cert.options?.[0] || cert.label;
            onRequirementClick(certType, cert.options);
          }
        }}
      >
        {isMet ? (
          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          <p className={`text-sm ${isMet ? 'text-gray-600' : 'text-gray-900'}`}>
            {cert.label}
            {cert.options && cert.options.length > 1 && (
              <span className="text-xs text-gray-500 ml-1">
                ({cert.options.length} options)
              </span>
            )}
          </p>
          {cert.note && (
            <p className="text-xs text-gray-500">{cert.note}</p>
          )}
          {cert.type === 'medical' && (
            <p className="text-xs text-gray-600 mt-1">
              Enter expiry date in Medical Certificate section above
            </p>
          )}
          {cert.options && cert.options.length > 1 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-700">Acceptable certificates:</p>
              <ul className="mt-1 text-xs text-gray-600 list-disc list-inside">
                {cert.options.map((opt, i) => (
                  <li key={i}>{opt}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {!isMet && onRequirementClick && (
          <Plus className="h-4 w-4 text-cyan-600 flex-shrink-0 mt-0.5" />
        )}
      </div>
    );
  };

  // Render vessel-level requirement with info icon (no red alert - AT_LEAST_1 certs)
  const renderVesselLevelRequirement = (cert, index) => {
    const isMet = isCertificateRequirementMet(cert, formData);

    return (
      <div
        key={index}
        className={`flex items-start gap-2 py-1.5 ${onRequirementClick ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded' : ''}`}
        onClick={() => {
          if (onRequirementClick) {
            const certType = cert.options?.[0] || cert.label;
            onRequirementClick(certType, cert.options);
          }
        }}
      >
        {isMet ? (
          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <Info className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          <p className={`text-sm ${isMet ? 'text-gray-600' : 'text-gray-700'}`}>
            {cert.label}
            {cert.options && cert.options.length > 1 && (
              <span className="text-xs text-gray-500 ml-1">
                ({cert.options.length} options)
              </span>
            )}
          </p>
          {cert.note && (
            <p className="text-xs text-gray-500">{cert.note}</p>
          )}
          {cert.options && cert.options.length > 1 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-700">Acceptable certificates:</p>
              <ul className="mt-1 text-xs text-gray-600 list-disc list-inside">
                {cert.options.map((opt, i) => (
                  <li key={i}>{opt}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {onRequirementClick && (
          <Plus className="h-4 w-4 text-cyan-600 flex-shrink-0 mt-0.5" />
        )}
      </div>
    );
  };

  if (!position) {
    return (
      <div className="text-sm text-gray-500 italic">
        Select a position to see certificate requirements
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mandatory for All Crew */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">
          {requirements.mandatoryForAll.title}
        </h4>
        <div className="space-y-1">
          {requirements.mandatoryForAll.certificates.map((cert, index) =>
            renderRequirement(cert, `mandatory-${index}`)
          )}
        </div>
      </div>

      {/* Position Specific */}
      {requirements.positionSpecific.certificates.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-amber-800 mb-2">
            {requirements.positionSpecific.title}
          </h4>
          <div className="space-y-1">
            {requirements.positionSpecific.certificates.map((cert, index) =>
              renderRequirement(cert, `position-${index}`)
            )}
          </div>
        </div>
      )}

      {/* Vessel Level Requirements (AT_LEAST_1 per WBC3) */}
      {requirements.vesselLevel && requirements.vesselLevel.certificates.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">
            {requirements.vesselLevel.title}
          </h4>
          <div className="space-y-1">
            {requirements.vesselLevel.certificates.map((cert, index) =>
              renderVesselLevelRequirement(cert, `vessel-${index}`)
            )}
          </div>
          <p className="text-xs text-slate-500 mt-3 italic">
            These certificates are not required for every crew member. WBC3 requires at least one person onboard to hold each. Ensure your crewing arrangements always include a qualified person.
          </p>
        </div>
      )}

      {/* Click hint */}
      {onRequirementClick && (
        <p className="text-xs text-gray-500 italic">
          Click any missing requirement to add it directly
        </p>
      )}
    </div>
  );
};

export default CertificateRequirements;
